import React, { ReactElement, use, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Add, DeleteForeverOutlined } from '@mui/icons-material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { Button, Divider, IconButton, TextField, Typography } from '@mui/material';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import MultiValueSelect from 'shared/components/formFields/multi-select-field';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PageHeader from 'shared/components/page-header/page-header';
import KeyValuePair from 'shared/models/key-value-pair';

import SettingTabs from 'features/settings/settings-tabs';
import useMalfunctionNotificationSettings from '../context/malfunction-notification-settings-hook';
import { FaultTypes, MalfunctionNotificationRecipient } from '../domain/models/malfunction-notification';
import MalfunctionNotificationCommand from '../domain/models/malfunction-notification-command';
import useRecipientValidation from '../hooks/recipient-validation-hook';
import useSortedRecipients from '../hooks/sorted-recipients-hook';

import './malfunction-notification-settings.scss';

function MalfunctionNotificationSettings(): ReactElement {
  const { t } = useTranslation('malfunctionNotifications');
  const { enablePopUpGuard } = use(ConfirmationPopupProviderContext);
  const { malfunctionNotifications, malfunctionNotificationIsLoading, handleSave } = useMalfunctionNotificationSettings();
  const [isRecipientsEmpty, setIsRecipientsEmpty] = useState(true);

  const getFaultTypes = (): KeyValuePair[] =>
    Object.entries(FaultTypes).map(([key, value]) => {
      return {
        key,
        value: t(`notifications.faultTypes.${value}`),
      };
    });

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<{
    recipients: MalfunctionNotificationRecipient[];
  }>({
    mode: 'onBlur',
  });

  const watchFields = watch('recipients');
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'recipients',
  });

  const { isValidEmail, isUniqueEmail } = useRecipientValidation();
  const sortedRecipients = useSortedRecipients(fields);
  const noRecipients = isRecipientsEmpty && fields.length < 1 && !malfunctionNotificationIsLoading;

  const registerOptions = {
    email: {
      required: t('notifications.email.required'),
      validate: {
        isValidEmail,
        isUniqueEmail: (value: string) => isUniqueEmail(value, watchFields),
      },
    },
    FaultTypes: {
      required: t('notifications.faultTypes.required'),
    },
  };

  const onSubmit: SubmitHandler<{
    recipients: MalfunctionNotificationRecipient[];
  }> = (data) => {
    const notification: MalfunctionNotificationCommand = {
      isActivelyMailing: true,
      recipients: data.recipients,
    };

    handleSave(notification);
  };

  useEffect(() => {
    if (malfunctionNotifications) {
      const allRecipients = malfunctionNotifications.flatMap((notification) =>
        notification.recipients.map((recipient) => ({
          id: recipient.id,
          templateId: recipient.templateId,
          email: recipient.email,
          faultTypes: recipient.faultTypes,
        })),
      );

      setValue('recipients', [...allRecipients]);
      reset({ recipients: [...allRecipients] });
      setIsRecipientsEmpty(allRecipients.length === 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [malfunctionNotifications, setValue, getValues]);

  useEffect(() => {
    enablePopUpGuard(isDirty);
  }, [isDirty]);

  return (
    <>
      <PageHeader title={t('pageHeaderTitle')} navigationComponent={<SettingTabs />} />
      <div className="malfunction-notifications-content">
        <div className="save-settings">
          <Typography variant="h5">{t('notifications.title')}</Typography>
          <Button disabled={!isDirty} startIcon={<CheckRounded />} variant="contained" type="submit" onClick={handleSubmit(onSubmit)}>
            {t('notifications.save')}
          </Button>
        </div>
        <div className="settings">
          <Typography variant="body1">{t('notifications.subTitle.email')}</Typography>
          <Typography variant="body1">{t('notifications.subTitle.faultTypes')}</Typography>
          <form>
            {fields.length === 0 && <Divider />}
            {!malfunctionNotificationIsLoading && noRecipients && (
              <div className="no-recipients">
                <Typography variant="body1">{t('notifications.empty')}</Typography>
              </div>
            )}
            {malfunctionNotificationIsLoading && <LoadingIndicator />}
            {sortedRecipients.map(({ field, index }) => {
              const error = errors.recipients?.[index];
              return (
                <React.Fragment key={field.id}>
                  <Divider />
                  <div className="row">
                    <Controller
                      name={`recipients.${index}.email`}
                      control={control}
                      rules={registerOptions.email}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t('notifications.email.label')}
                          variant="outlined"
                          error={!!error?.email}
                          helperText={error?.email?.message}
                        />
                      )}
                    />
                    <Controller
                      name={`recipients.${index}.faultTypes`}
                      control={control}
                      rules={registerOptions.FaultTypes}
                      render={({ field }) => (
                        <MultiValueSelect
                          field={{ ...field }}
                          label={t('notifications.faultTypes.label')}
                          options={getFaultTypes()}
                          error={!!error?.faultTypes}
                          helperText={error?.faultTypes?.message}
                        />
                      )}
                    />
                    <IconButton className="remove-button" onClick={() => remove(index)}>
                      <DeleteForeverOutlined />
                    </IconButton>
                  </div>
                </React.Fragment>
              );
            })}
            <Button
              variant="text"
              startIcon={<Add />}
              onClick={() =>
                append({
                  email: '',
                  faultTypes: [],
                })
              }
            >
              {t('notifications.add')}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
}

export default MalfunctionNotificationSettings;
